import { logger } from '@zep/core-logger';

export type ClassroomAnalyticsContentType = 'QUIZSET' | 'BOARD';

/**
 * classroom 분석 이벤트가 발생한 위치(source).
 *
 * 원칙: UI 가 실제 존재하는 "가장 가까운 컴포넌트" 를 가리킨다.
 * 모달에서 발생한 이벤트는 그 모달을 source 로 쓰고, 모달을 어디서 열었는지는
 * 직전 opening 이벤트(예: `click_link_to_classroom`)의 source 로 추적한다.
 */
export type ClassroomSource =
  | 'gnb_my_menu' // GNB 내 메뉴 드롭다운
  | 'profile_sidebar' // 프로필 사이드바
  | 'classroom_list' // 학급 목록 페이지
  | 'classroom_detail' // 학급 상세 페이지(레이아웃)
  | 'classroom_detail_header' // 학급 상세 헤더 카드
  | 'classroom_quizzes_tab' // 상세 > 퀴즈 탭
  | 'classroom_boards_tab' // 상세 > 보드 탭
  | 'classroom_links_tab' // 상세 > 링크 탭(외부 링크 공유)
  | 'classroom_students_tab' // 상세 > 학생 탭
  | 'classroom_assignments_tab' // 상세 > 과제 탭(퀴즈·보드·자료 통합)
  | 'classroom_student_detail' // 학생 상세 페이지
  | 'classroom_assignment_submissions' // 과제 > 제출 현황 화면
  | 'classroom_join_page' // /classrooms/[id]/join 참여 페이지
  | 'join_page' // /join 코드 입력 페이지
  | 'quiz_detail' // /quiz/[id] 상세 CTA
  | 'my_quiz_list' // 내 퀴즈 목록 행 「⋮」 메뉴
  | 'create_classroom_modal'
  | 'created_classroom_modal'
  | 'rename_classroom_modal'
  | 'invite_classroom_modal'
  | 'classroom_link_modal'
  | 'classroom_link_content_modal' // 학급에서 콘텐츠(퀴즈/보드) 연결하는 모달
  | 'classroom_promotion_modal'
  | 'point_intro_modal' // 포인트 최초 안내 모달
  | 'point_release_modal' // 포인트 출시 안내 모달
  | 'quiz_result_modal'
  | 'content_share_modal' // 공유 모달 내 학급 연결 섹션
  | 'quiz_saved_modal' // 새 퀴즈 저장 직후 클래스 연결 권유 모달
  | 'jelly_record_modal' // 플레이 젤리 지급 직후 클래스 연결 권유 모달
  | 'play_share' // 플레이 공유 팝오버 클래스 줄
  | 'classroom_student_manage' // 학생 관리 테이블(/students/manage)
  | 'student_qr_modal' // 학생 QR 크게 보기 모달
  | 'add_student_modal' // 학생 추가 방법 선택 모달
  // ClassNamePrompt surface — 학생 표시이름 입력 강제 모달 진입 경로
  | 'board' // 보드 상세 페이지(BoardDetailPageView)
  | 'play' // 플레이 iframe(PlayIframe)
  | 'classroom_board_tab' // 학급 상세 > 보드 탭(StudentBoards)
  | 'classroom_quiz_tab'; // 학급 상세 > 퀴즈 탭(StudentQuizzes)

export interface ClassroomAnalyticsParams {
  source: ClassroomSource;
  role?: 'owner' | 'member';
  classroomId?: string;
  [key: string]: unknown;
}

export const trackClassroomEvent = (
  eventName: string,
  params: ClassroomAnalyticsParams,
) => {
  logger.track(eventName, {
    feature: 'classroom',
    ...params,
  });
};

/**
 * content 이벤트 이름에 박는 콘텐츠 타입 토큰.
 * 예) `click_classroom_${token}_enter` → click_classroom_quiz_enter / click_classroom_board_enter
 */
export const classroomContentToken = (
  contentType: ClassroomAnalyticsContentType,
): 'quiz' | 'board' => (contentType === 'QUIZSET' ? 'quiz' : 'board');
